import { FC, useState } from 'react';

import {
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    FileUploader,
    Modal,
    Icon,
    Tooltip
} from 'common/components';
import { ButtonVariant, CategoryField, EntityType } from 'common/enums';
import { CategoryDTO, FieldError, FileDetails } from 'common/models';
import {
    ACTION_ITEMS,
    CATEGORY_DESCRIPTION_MAX_LENGTH,
    CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    CATEGORY_NAME_INVALID_CHAR_ERROR,
    CATEGORY_NAME_MAX_LENGTH,
    CATEGORY_NAME_MAX_LENGTH_ERROR,
    CATEGORY_NAME_EMPTY_ERROR,
    CATEGORY_ORDER_EMPTY_ERROR,
    FILE_COUNT_MAX,
    CATEGORY_ORDER_GREATER_THAN_ZERO,
    CATEGORY_ORDER_INVALID_ERROR,
    FILE_COUNT_ERROR,
    FILE_TYPE_ERROR,
    CATEGORY_ORDER_MAX_LENGTH,
    CATEGORY_ORDER_MAX_LENGTH_ERROR
} from 'common/config';
import { addValidationError, removeValidationError } from 'common/utils/error';
import RadioButton from 'common/components/base/radio-button';
import RadioButtonGroup from 'common/components/base/radio-button-group';
import CategoryForm from '../category-form/CategoryForm';
import DownloadTemplate from '../download-template/DownloadTemplate';
import { validateInputString, validateMaxLength, validateNumbers } from 'common/utils/validator';

import './CategoryAddMaster.scss';
import { useDispatch } from 'react-redux';
import { fileUploadActions } from 'common/redux/file-upload';
import { isAllowedFileType } from 'common/utils/file';
import { FileUploadVariants } from 'common/enums/variants.enum';
import { ICON_COLLECTION } from 'assets/media/icons';

type CategoryAddMasterProps = {
    fieldErrors: Array<FieldError>;
    isUploadDisabled: boolean;
    isVisible: boolean;
    uploadingFiles: Array<FileDetails>;
    selectedFiles: Array<FileDetails>;
    uploadedUser: string;
    onClose: () => void;
    onAdd: (addedData: CategoryDTO) => void;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    onSelectedFileRemove: (file: FileDetails) => void;
};

const CategoryAddMaster: FC<CategoryAddMasterProps> = ({
    fieldErrors,
    isUploadDisabled,
    uploadingFiles,
    selectedFiles,
    isVisible,
    uploadedUser,
    onClose,
    onAdd,
    updateFieldErrors,
    onSelectedFileRemove
}) => {
    const [name, setName] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [description, setDescription] = useState('');
    const [radioButtonSelected, setRadioButton] = useState('inputData');
    const [uploadError, setUploadError] = useState<string>('');
    const dispatch = useDispatch();

    const handleAdd = () => {
        if (radioButtonSelected === 'inputData') {
            let errors = fieldErrors;
            if (!name.trim()) {
                errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_EMPTY_ERROR);
            }
            if (!sortOrder) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    CATEGORY_ORDER_EMPTY_ERROR
                );
            }
            if (errors.length === 0) {
                const addedData: CategoryDTO = {
                    categoryId: '',
                    categoryName: name.trim(),
                    categoryOrder: Number(sortOrder),
                    description: description
                };
                onAdd(addedData);
            }
            updateFieldErrors(errors);
        } else {
            handleUpload();
        }
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        if (validateMaxLength(event.target.value, CATEGORY_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_EMPTY_ERROR);
        }
        if (validateMaxLength(event.target.value, CATEGORY_NAME_MAX_LENGTH)) {
            errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_MAX_LENGTH_ERROR);
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        if (value !== '') {
            if (!validateNumbers(value)) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    CATEGORY_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(value) === 0) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        CATEGORY_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(value) > CATEGORY_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        CATEGORY_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        updateFieldErrors(errors);
        setSortOrder(value);
    };

    const handleChange = (event: any) => {
        setRadioButton(event.target.value);
    };

    const handleFileDrop = (files: Array<FileDetails>) => {
        let isAllowedType = true;

        setUploadError('');
        dispatch(fileUploadActions.setSelectedFiles([]));

        if (files.length > FILE_COUNT_MAX) {
            setUploadError(FILE_COUNT_ERROR);
        } else {
            const newFiles = files.map((item: FileDetails) => {
                item.targetID = '';
                isAllowedType = isAllowedFileType(item, /(\.xls|\.xlsx)$/i);
                return item;
            });
            if (isAllowedType) {
                dispatch(fileUploadActions.setSelectedFiles(newFiles));
            } else {
                setUploadError(FILE_TYPE_ERROR);
            }
        }
    };

    const handleUpload = () => {
        dispatch(
            fileUploadActions.uploadFile({
                files: selectedFiles,
                variantName: FileUploadVariants.MasterData,
                targetId: '',
                entityType: EntityType.Category
            })
        );
    };

    const handleClose = () => {
        setUploadError('');
        onClose();
    };

    const getSaveDisabledStatus = () => {
        if (radioButtonSelected === 'uploadFile') {
            return selectedFiles?.length < 1;
        } else {
            return fieldErrors.length > 0;
        }
    };

    return (
        <Modal
            show={isVisible}
            onClose={onClose}
            style={{ width: '51vw' }}
            className="add-indicator-modal">
            <ModalHeader className="category-add-header">{'Add new category'}</ModalHeader>
            <ModalBody className="category-add-body">
                <div className="category-body-header">
                    <RadioButtonGroup className="category-radio-button-group">
                        <RadioButton
                            name="radioButton"
                            id="inputData"
                            value="inputData"
                            className="category-radio-button"
                            checked={radioButtonSelected === 'inputData'}
                            onChange={(event) => handleChange(event)}>
                            Input Data
                        </RadioButton>
                        <RadioButton
                            name="radioButton"
                            id="uploadFile"
                            value="uploadFile"
                            className="category-radio-button upload"
                            checked={radioButtonSelected === 'uploadFile'}
                            disabled={isUploadDisabled}
                            onChange={(event) => handleChange(event)}>
                            Upload file
                        </RadioButton>
                        {isUploadDisabled && (
                            <Tooltip
                                trigger={
                                    <Icon
                                        src={ICON_COLLECTION.IC_Help_Outline}
                                        className="upload-disabled-help"
                                        role="tooltip"
                                    />
                                }
                                placement="right"
                                hideCloseButton
                                variant="alt"
                                hideArrow={true}
                                allowClick={false}>
                                {`Upload is in progress, locked by ${uploadedUser}`}
                            </Tooltip>
                        )}
                    </RadioButtonGroup>

                    {radioButtonSelected === 'uploadFile' && <DownloadTemplate fromMaster={true} />}
                </div>
                {radioButtonSelected === 'inputData' ? (
                    <CategoryForm
                        fieldErrors={fieldErrors}
                        name={name}
                        handleNameChange={handleNameChange}
                        sortOrder={sortOrder}
                        handleSortOrderChange={handleSortOrderChange}
                        description={description}
                        handleDescription={handleDescription}
                    />
                ) : (
                    <FileUploader
                        maxFiles={FILE_COUNT_MAX}
                        uploadingFiles={uploadingFiles}
                        selectedFiles={selectedFiles}
                        errorMessage={uploadError}
                        onSelectedFileRemove={onSelectedFileRemove}
                        onDrop={handleFileDrop}
                    />
                )}
            </ModalBody>
            <ModalFooter className="category-add-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    onClick={handleAdd}
                    disabled={getSaveDisabledStatus()}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={handleClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CategoryAddMaster;
